import type { AvailabilityInterval, AvailabilityWeight } from 'components/TrainingPlanner/types';

/** 24:00 as a minute offset — the exclusive end of a weekday. */
export const DAY_MINUTES = 1440;

export const AVAILABLE: AvailabilityWeight = 1;
export const PREFERRED: AvailabilityWeight = 2;

/** The editable shape: minutes only, since the grid never draws a string. */
export interface Interval {
    weekday: number;
    start: number;
    end: number;
    weight: AvailabilityWeight;
}

export const minutesToTime = (minutes: number) =>
    `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

export const timeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(':');
    return Number(hours) * 60 + Number(minutes ?? 0);
};

export const fromApi = (intervals: AvailabilityInterval[] = []): Interval[] =>
    intervals.map((interval) => ({
        weekday: interval.weekday,
        start: interval.start_minute,
        end: interval.end_minute,
        weight: interval.weight
    }));

export const toApi = (intervals: Interval[]) =>
    intervals.map((interval) => ({
        weekday: interval.weekday,
        start_time: minutesToTime(interval.start),
        end_time: minutesToTime(interval.end),
        weight: interval.weight
    }));

/**
 * Sorted, non overlapping, neighbours of equal weight merged.
 *
 * The same normal form the server stores, so what the editor shows after a paint
 * is what comes back after a save. Overlaps of different weight are resolved
 * rather than dropped: painting "would like to" across a stretch that was merely
 * possible upgrades exactly that stretch and leaves the rest alone.
 */
export const canonicalize = (intervals: Interval[]): Interval[] => {
    const days = new Map<number, Interval[]>();

    intervals.forEach((interval) => {
        const start = Math.max(0, interval.start);
        const end = Math.min(DAY_MINUTES, interval.end);

        // An empty or inverted stretch carries no statement. Dropping it here is
        // what lets everything below assume start < end.
        if (end <= start) return;

        days.set(interval.weekday, [...(days.get(interval.weekday) ?? []), { ...interval, start, end }]);
    });

    return [...days.keys()]
        .sort((a, b) => a - b)
        .flatMap((weekday) => canonicalizeDay(weekday, days.get(weekday) ?? []));
};

const canonicalizeDay = (weekday: number, intervals: Interval[]): Interval[] => {
    const points = [...new Set(intervals.flatMap((interval) => [interval.start, interval.end]))].sort((a, b) => a - b);
    const result: Interval[] = [];

    // Every boundary splits the day into segments that lie either fully inside an
    // interval or fully outside it, which is what makes overlapping weights
    // resolvable instead of ambiguous.
    for (let index = 0; index < points.length - 1; index++) {
        const start = points[index];
        const end = points[index + 1];
        const covering = intervals.filter((interval) => interval.start <= start && interval.end >= end);

        if (covering.length === 0) continue;

        const weight = Math.max(...covering.map((interval) => interval.weight)) as AvailabilityWeight;
        const last = result[result.length - 1];

        if (last && last.end === start && last.weight === weight) last.end = end;
        else result.push({ weekday, start, end, weight });
    }

    return result;
};

/**
 * Paints or erases one stretch of one weekday.
 *
 * Always cuts the range out first and only then adds it back, so painting over
 * a different weight replaces it instead of layering — which is what makes a
 * single drag over mixed ground produce one uniform stretch.
 */
export const setRange = (
    intervals: Interval[],
    weekday: number,
    start: number,
    end: number,
    weight: AvailabilityWeight | null
): Interval[] => {
    if (end <= start) return intervals;

    const kept = intervals.flatMap((interval) => {
        if (interval.weekday !== weekday || interval.end <= start || interval.start >= end) return [interval];

        const pieces: Interval[] = [];
        if (interval.start < start) pieces.push({ ...interval, end: start });
        if (interval.end > end) pieces.push({ ...interval, start: end });

        return pieces;
    });

    return canonicalize(weight ? [...kept, { weekday, start, end, weight }] : kept);
};

/** The weight covering a cell, or null when the cell is free. */
export const weightAt = (intervals: Interval[], weekday: number, minute: number): AvailabilityWeight | null =>
    intervals.find((interval) => interval.weekday === weekday && interval.start <= minute && interval.end > minute)
        ?.weight ?? null;

/** Total minutes offered across the week, overlaps counted once. */
export const totalMinutes = (intervals: Interval[]) =>
    canonicalize(intervals).reduce((sum, interval) => sum + (interval.end - interval.start), 0);

/**
 * "16:00–18:00" — the shape a planner reads a stated time in.
 *
 * The seconds a TIME column carries never get here, and the en dash is the range
 * dash rather than a hyphen, so "16:00-18:00" cannot be misread as a minus.
 */
export const formatRange = (interval: Interval) => `${minutesToTime(interval.start)}–${minutesToTime(interval.end)}`;

/**
 * Canonical intervals grouped by weekday, in week order.
 *
 * A list ordered by day is what makes a row scannable: "Di 16:00–18:00" beside
 * "Do 17:00–19:00" answers when somebody can train, where a set of weekday
 * initials only answers whether they can at all.
 */
export const byWeekday = (intervals: Interval[]): Array<{ weekday: number; ranges: Interval[] }> => {
    const grouped = new Map<number, Interval[]>();

    canonicalize(intervals).forEach((interval) => {
        grouped.set(interval.weekday, [...(grouped.get(interval.weekday) ?? []), interval]);
    });

    return [...grouped.entries()].sort(([a], [b]) => a - b).map(([weekday, ranges]) => ({ weekday, ranges }));
};

/** "3 Std. 30 Min." — hours alone read as precise when they are not. */
export const formatDuration = (minutes: number, hourLabel: string, minuteLabel: string) => {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;

    if (hours === 0) return `${rest} ${minuteLabel}`;
    if (rest === 0) return `${hours} ${hourLabel}`;

    return `${hours} ${hourLabel} ${rest} ${minuteLabel}`;
};
